import { useEffect, useState } from "react";
import { RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { FileDown } from "lucide-react";
import { inputCls } from "../Login";

const card = "rounded-2xl border border-adminborder bg-admincard";
const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3.5 py-2.5 text-sm text-slate-200 outline-none focus:border-saffron";

const STATUSES = ["", "Order Created", "Pickup Scheduled", "Pickup Assigned", "Picked Up", "In Transit",
  "Reached Hub", "Out for Delivery", "Delivered", "NDR", "RTO Initiated", "RTO In Transit", "RTO Delivered", "Cancelled"];

export default function AdminShipments() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [status, setStatus] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState({});
  const [pendingOnly, setPendingOnly] = useState(false);
  const [q, setQ] = useState("");
  const [fCountry, setFCountry] = useState("");
  const [fCustomer, setFCustomer] = useState("");
  const [fCourier, setFCourier] = useState("");
  const [applied, setApplied] = useState({ country: "", customer: "", courier: "" });
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [update, setUpdate] = useState({ status: "", location: "", description: "" });
  const [editIntl, setEditIntl] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [courierOpts, setCourierOpts] = useState([]);
  const [liveEvents, setLiveEvents] = useState([]);
  const [liveLoading, setLiveLoading] = useState(false);
  const [trackLive, setTrackLive] = useState(null);
  const [trackLiveLoading, setTrackLiveLoading] = useState(false);

  const openTracking = (s) => {
    setEditing(s);
    setUpdate({ status: s.status, location: "", description: "" });
    setTrackLive(null);
    fetchLive(s);
  };
  const fetchLive = async (s) => {
    setTrackLiveLoading(true);
    try { const r = await api.get(`/admin/shipments/${s.id}/live-track`); setTrackLive(r.data); }
    catch { setTrackLive({ tracking: s.tracking || [], status: s.status, live: false }); }
    finally { setTrackLiveLoading(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete shipment ${s.awb} (${s.customer_name || ""}) permanently? This cannot be undone.`)) return;
    try { await api.delete(`/admin/shipments/${s.id}`); toast.success(`Shipment ${s.awb} deleted`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const toggleSel = (id) => setSelected((p) => { const nx = { ...p }; if (nx[id]) delete nx[id]; else nx[id] = true; return nx; });
  const selCount = Object.keys(selected).length;
  const allChecked = data.items.length > 0 && data.items.every((s) => selected[s.id]);
  const toggleAll = () => setSelected(allChecked ? {} : Object.fromEntries(data.items.map((s) => [s.id, true])));

  const downloadManifest = async () => {
    const ids = Object.keys(selected);
    try {
      const res = ids.length
        ? await api.post("/reports/manifest.pdf", { ids }, { responseType: "blob" })
        : await api.get(`/reports/manifest.pdf?date=${dateFrom || new Date().toISOString().slice(0, 10)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `INDIAFAST-manifest-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(ids.length ? `Manifest generated for ${ids.length} selected shipment(s)` : "Day-end manifest downloaded");
      setSelected({});
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Manifest download failed"); }
  };

  const downloadInvoice = async (s) => {
    try {
      const res = await api.get(`/shipments/${s.id}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `INDIAFAST-invoice-${s.awb}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Invoice downloaded for ${s.awb}`);
    } catch {
      toast.error(`Invoice not generated yet for ${s.awb}`);
    }
  };

  const openEdit = (s) => {
    setEditIntl(s);
    if (s.tracking_number) {
      setLiveLoading(true);
      setLiveEvents([]);
      api.get(`/shipments/${s.id}`)
        .then((r) => setLiveEvents(r.data.tracking || []))
        .catch(() => {})
        .finally(() => setLiveLoading(false));
    } else {
      setLiveEvents([]);
    }
    setEditForm({
      tracking_number: s.tracking_number || "", service_name: s.service_name || "",
      service_type: s.service_type || "express", shipping_date: s.shipping_date || "",
      received_from_customer: s.received_from_customer ?? "", paid_to_indiafast: s.paid_to_indiafast ?? "",
      payment_mode: s.payment_mode || "cash", status: s.status, customer_price: s.customer_price ?? "",
      awb: s.awb || "", courier_name: s.courier_name || "", base_price: s.base_price ?? "", agent_commission: s.agent_commission ?? "",
      weight: s.weight ?? "", chargeable_weight: s.chargeable_weight ?? "",
      total_value: s.total_value ?? "", cod_amount: s.cod_amount ?? "",
      payment_type: s.payment_type || "PREPAID", expected_delivery: s.expected_delivery || "",
      notes: s.notes || "",
      del_name: s.delivery?.name || "", del_phone: s.delivery?.phone || "",
      del_address: s.delivery?.address || "", del_city: s.delivery?.city || "",
      del_zipcode: s.delivery?.zipcode || s.delivery?.pincode || "", del_country: s.delivery?.country || "",
      snd_name: s.pickup?.name || "", snd_phone: s.pickup?.phone || "",
      snd_address: s.pickup?.address || "", snd_city: s.pickup?.city || "",
      snd_pincode: s.pickup?.pincode || "", snd_state: s.pickup?.state || "",
    });
  };

  const saveEdit = async () => {
    try {
      const putRes = await api.put(`/admin/shipments/${editIntl.id}`, {
        tracking_number: editForm.tracking_number || null,
        service_name: editForm.service_name, service_type: editForm.service_type,
        shipping_date: editForm.shipping_date || null,
        received_from_customer: parseFloat(editForm.received_from_customer) || 0,
        paid_to_indiafast: parseFloat(editForm.paid_to_indiafast) || 0,
        payment_mode: editForm.payment_mode, status: editForm.status,
        customer_price: parseFloat(editForm.customer_price) || 0,
        base_price: parseFloat(editForm.base_price) || 0,
        agent_commission: parseFloat(editForm.agent_commission) || 0,
        awb: editForm.awb || editIntl.awb,
        courier_name: editForm.courier_name,
        weight: parseFloat(editForm.weight) || 0,
        chargeable_weight: parseFloat(editForm.chargeable_weight) || 0,
        total_value: parseFloat(editForm.total_value) || 0,
        cod_amount: parseFloat(editForm.cod_amount) || 0,
        payment_type: editForm.payment_type,
        expected_delivery: editForm.expected_delivery || null,
        notes: editForm.notes || "",
        delivery: { ...editIntl.delivery, name: editForm.del_name, phone: editForm.del_phone,
                    address: editForm.del_address, city: editForm.del_city,
                    zipcode: editForm.del_zipcode, country: editForm.del_country },
        pickup: { ...(editIntl.pickup || {}), name: editForm.snd_name, phone: editForm.snd_phone,
                  address: editForm.snd_address, city: editForm.snd_city,
                  pincode: editForm.snd_pincode, state: editForm.snd_state },
      });
      const newCost = putRes.data?.admin_cost;
      toast.success("Shipment updated" + (editIntl.is_international && newCost != null ? ` — cost price re-fetched: ₹${newCost}` : ""));
      setEditIntl(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const setE = (k) => (e) => setEditForm({ ...editForm, [k]: e.target.value });

  const load = () => {
    const params = new URLSearchParams({ page, limit: 20 });
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    if (typeFilter) params.set("intl", typeFilter);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (pendingOnly) params.set("unmanifested", "true");
    if (applied.country) params.set("country", applied.country);
    if (applied.customer) params.set("customer", applied.customer);
    if (applied.courier) params.set("courier", applied.courier);
    api.get(`/admin/shipments?${params}`).then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/couriers").then((r) => setCourierOpts(r.data.map((c) => c.name))).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, q, page, typeFilter, dateFrom, dateTo, pendingOnly, applied]);

  const applyFilters = () => { setApplied({ country: fCountry.trim(), customer: fCustomer.trim(), courier: fCourier.trim() }); setPage(1); };
  const resetFilters = () => { setFCountry(""); setFCustomer(""); setFCourier(""); setApplied({ country: "", customer: "", courier: "" }); setPage(1); };
  const filterKey = (e) => { if (e.key === "Enter") applyFilters(); };

  const saveStatus = async () => {
    try {
      await api.post(`/admin/shipments/${editing.id}/status`, update);
      toast.success("Status updated");
      setEditing(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const pages = Math.max(1, Math.ceil(data.total / 20));

  return (
    <div data-testid="admin-shipments" className="space-y-6">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">Shipments Master</h1>

      <div className="flex flex-wrap items-center gap-3" data-testid="admin-exact-filters">
        <input data-testid="admin-filter-country" className={`${darkInput} max-w-[220px]`} placeholder="Search by Country (exact)…"
               value={fCountry} onChange={(e) => setFCountry(e.target.value)} onKeyDown={filterKey} />
        <input data-testid="admin-filter-customer" className={`${darkInput} max-w-[220px]`} placeholder="Search by Customer (exact)…"
               value={fCustomer} onChange={(e) => setFCustomer(e.target.value)} onKeyDown={filterKey} />
        <select data-testid="admin-filter-courier" className={`${darkInput} max-w-[220px]`} value={fCourier}
                onChange={(e) => setFCourier(e.target.value)}>
          <option value="">Search by Courier — All</option>
          {courierOpts.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={applyFilters} data-testid="admin-filter-apply-btn"
                className="rounded-lg bg-saffron px-5 py-2.5 text-xs font-bold text-white transition-colors hover:bg-saffron-hover">
          Search
        </button>
        <button onClick={resetFilters} data-testid="admin-filter-reset-btn"
                className="rounded-lg border border-adminborder px-4 py-2.5 text-xs font-bold text-slate-300 transition-colors hover:border-saffron hover:text-saffron">
          Reset
        </button>
        {(applied.country || applied.customer || applied.courier) && (
          <span className="rounded-full border border-saffron/40 bg-saffron/10 px-3 py-1.5 text-[11px] font-bold text-saffron" data-testid="admin-filter-active-badge">
            Exact match: {[applied.country, applied.customer, applied.courier].filter(Boolean).join(" · ")}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <input data-testid="admin-shipments-search" className={`${darkInput} max-w-xs`} placeholder="Search AWB / order / customer"
               value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        <select data-testid="admin-shipments-status-filter" className={`${darkInput} max-w-xs`} value={status}
                onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          {STATUSES.map((s) => <option key={s} value={s}>{s || "All Statuses"}</option>)}
        </select>
        <select data-testid="admin-shipments-type-filter" className={`${darkInput} max-w-xs`} value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
          <option value="">All Types</option>
          <option value="true">International</option>
          <option value="false">Domestic</option>
        </select>
        <input type="date" className={`${darkInput} max-w-[150px]`} data-testid="admin-shipments-date-from" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
        <input type="date" className={`${darkInput} max-w-[150px]`} data-testid="admin-shipments-date-to" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
        <button onClick={() => { setPendingOnly(!pendingOnly); setPage(1); }} data-testid="admin-pending-manifest-toggle"
                className={`rounded-lg border px-4 py-2 text-xs font-bold transition-colors ${pendingOnly ? "border-amber-400 bg-amber-500/15 text-amber-400" : "border-adminborder text-slate-300 hover:border-amber-400"}`}>
          Pending Manifest
        </button>
        <button onClick={downloadManifest} data-testid="admin-manifest-btn"
                className="rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover">
          {selCount ? `Generate Manifest (${selCount})` : "Day-End Manifest PDF"}
        </button>
        <button onClick={async () => {
          try {
            const r = await api.post("/admin/shipments/sync-statuses");
            toast.success(`Courier sync done — ${r.data.checked} checked, ${r.data.updated} status updated`);
            load();
          } catch (err) { toast.error(apiError(err)); }
        }} data-testid="admin-sync-statuses-btn"
                className="flex items-center gap-1.5 rounded-lg border border-adminborder px-4 py-2 text-xs font-bold text-slate-300 hover:border-saffron hover:text-saffron">
          <RefreshCw className="h-3.5 w-3.5" /> Sync Statuses
        </button>
        <button onClick={async () => {
          if (!window.confirm("Update cost prices for ALL international shipments from the Price Engine? (Domestic costs already come from Shipmozo.)")) return;
          try {
            const r = await api.post("/admin/shipments/backfill-costs");
            toast.success(`Cost updated for ${r.data.updated} international shipment(s) · ${r.data.zero_priced} with no rate card set to ₹0`);
            load();
          } catch (err) { toast.error(apiError(err)); }
        }} data-testid="admin-backfill-costs-btn"
                className="flex items-center gap-1.5 rounded-lg border border-adminborder px-4 py-2 text-xs font-bold text-slate-300 hover:border-saffron hover:text-saffron">
          <RefreshCw className="h-3.5 w-3.5" /> Sync Cost Prices
        </button>
      </div>

      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm" data-testid="admin-shipments-table">
          <thead>
            <tr className="border-b border-adminborder text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-3 py-3.5"><input type="checkbox" data-testid="admin-manifest-select-all" checked={allChecked} onChange={toggleAll} className="h-4 w-4 accent-saffron" /></th>
              <th className="px-5 py-3.5">Date</th><th className="px-5 py-3.5">AWB</th><th className="px-5 py-3.5">Customer</th><th className="px-5 py-3.5">Receiver (Consignee)</th><th className="px-5 py-3.5">Country</th>
              <th className="px-5 py-3.5">Courier</th><th className="px-5 py-3.5">Type</th><th className="px-5 py-3.5">Price / Cost</th><th className="px-5 py-3.5">Status</th><th className="px-5 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((s) => (
              <tr key={s.id} className="border-b border-adminborder/60 last:border-0 hover:bg-white/5">
                <td className="px-3 py-3.5"><input type="checkbox" data-testid={`manifest-check-${s.awb}`} checked={!!selected[s.id]} onChange={() => toggleSel(s.id)} className="h-4 w-4 accent-saffron" /></td>
                <td className="px-5 py-3.5 text-xs text-slate-400" data-testid={`date-cell-${s.awb}`}>{s.created_at ? new Date(s.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                <td className="px-5 py-3.5 font-mono text-saffron">{s.awb}{s.manifested ? <span className="ml-1.5 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-400" title={s.manifest_no || "Manifested"}>MF</span> : null}</td>
                <td className="px-5 py-3.5">{s.customer_name}{s.is_demo ? <span className="ml-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-400">DEMO</span> : null}</td>
                <td className="px-5 py-3.5" data-testid={`receiver-cell-${s.awb}`}>{s.delivery?.name || "—"}{s.delivery?.city ? <span className="block text-[11px] text-slate-500">{s.delivery.city}</span> : null}</td>
                <td className="px-5 py-3.5 font-semibold" data-testid={`country-cell-${s.awb}`}>{s.delivery?.country || s.destination_country || "India"}</td>
                <td className="px-5 py-3.5">{s.courier_name}</td>
                <td className="px-5 py-3.5"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${s.is_international ? "bg-blue-500/15 text-blue-400" : "bg-emerald-500/15 text-emerald-400"}`}>{s.is_international ? "INTL" : "DOM"}</span></td>
                <td className="px-5 py-3.5">₹{s.customer_price} <span className="text-slate-500">/ ₹{s.cost_price ?? s.base_price ?? 0}</span>
                  {s.agent_commission > 0 && <div className="mt-0.5 text-[10px] font-bold text-blue-400">+ agent comm ₹{s.agent_commission}</div>}
                </td>
                <td className="px-5 py-3.5"><span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-bold">{s.status}</span></td>
                <td className="px-5 py-3.5">
                  <div className="flex gap-2">
                    <button onClick={() => openTracking(s)}
                            data-testid={`admin-edit-status-${s.awb}`}
                            className="rounded-lg border border-adminborder px-3 py-1.5 text-xs font-bold text-slate-300 hover:border-saffron hover:text-saffron">
                      Status
                    </button>
                    <button onClick={() => openEdit(s)} data-testid={`admin-edit-intl-${s.awb}`}
                            className="rounded-lg border border-blue-500/40 px-3 py-1.5 text-xs font-bold text-blue-400 hover:bg-blue-500/10">
                      Edit
                    </button>
                    <button onClick={() => downloadInvoice(s)} data-testid={`admin-invoice-dl-${s.awb}`}
                            title="Download invoice PDF"
                            className="rounded-lg border border-emerald-500/40 px-3 py-1.5 text-xs font-bold text-emerald-400 hover:bg-emerald-500/10">
                      <FileDown className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => del(s)} data-testid={`admin-delete-shipment-${s.awb}`}
                            className="rounded-lg border border-red-500/40 px-3 py-1.5 text-xs font-bold text-red-400 hover:bg-red-500/10">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={10} className="px-5 py-12 text-center text-slate-500">No shipments found.</td></tr>}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex gap-2">
          {Array.from({ length: Math.min(pages, 10) }, (_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
                    className={`h-9 w-9 rounded-lg text-sm font-bold ${page === i + 1 ? "bg-saffron text-white" : "border border-adminborder text-slate-400"}`}>{i + 1}</button>
          ))}
        </div>
      )}

      {editIntl && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4" data-testid="intl-edit-modal">
          <div className={`${card} mx-auto my-8 w-full max-w-2xl p-6`}>
            <h3 className="font-heading text-lg font-bold text-white">Edit Shipment — <span className="font-mono text-saffron">{editIntl.awb}</span></h3>
            <p className="mt-1 text-xs text-slate-500">Admin can edit all fields. Tracking number is assigned here after courier handover.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Courier Tracking Number</label>
                <input className={darkInput} data-testid="edit-tracking-number" placeholder="e.g. 1Z999AA10123456784" value={editForm.tracking_number} onChange={setE("tracking_number")} />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Courier</label>
                <select className={darkInput} data-testid="edit-courier-name" value={editForm.courier_name}
                        onChange={(e) => setEditForm({ ...editForm, courier_name: e.target.value, service_name: e.target.value })}>
                  {[...new Set([editForm.courier_name, ...courierOpts].filter(Boolean))].map((cn) => <option key={cn} value={cn}>{cn}</option>)}
                </select>
              </div>
              <select className={darkInput} data-testid="edit-service-type" value={editForm.service_type} onChange={setE("service_type")}>
                {["express", "dutypaid", "eco", "expedite", "self", "medicine", "document"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Shipping Date</label>
                <input type="date" className={darkInput} data-testid="edit-shipping-date" value={editForm.shipping_date} onChange={setE("shipping_date")} />
              </div>
              <select className={darkInput} data-testid="edit-status" value={editForm.status} onChange={setE("status")}>
                {STATUSES.filter(Boolean).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input type="number" className={darkInput} data-testid="edit-received" placeholder="Received from customer ₹" value={editForm.received_from_customer} onChange={setE("received_from_customer")} />
              <input type="number" className={darkInput} data-testid="edit-paid" placeholder="Paid to INDIAFAST ₹" value={editForm.paid_to_indiafast} onChange={setE("paid_to_indiafast")} />
              <select className={darkInput} data-testid="edit-payment-mode" value={editForm.payment_mode} onChange={setE("payment_mode")}>
                <option value="razorpay">Razorpay</option><option value="cash">Cash</option><option value="upi">UPI</option>
              </select>
              <input type="number" className={darkInput} data-testid="edit-customer-price" placeholder="Customer price ₹" value={editForm.customer_price} onChange={setE("customer_price")} />
              <input className={darkInput} data-testid="edit-awb" placeholder="AWB" value={editForm.awb} onChange={setE("awb")} />
              <input className={darkInput} data-testid="edit-tracking-number" placeholder="Carrier tracking number" value={editForm.tracking_number} onChange={setE("tracking_number")} />
              <input type="number" className={darkInput} data-testid="edit-base-price" placeholder="Base cost ₹" value={editForm.base_price} onChange={setE("base_price")} />
              <input type="number" className={darkInput} data-testid="edit-agent-commission" placeholder="Agent commission ₹" value={editForm.agent_commission} onChange={setE("agent_commission")} />
              <input type="number" step="0.01" className={darkInput} data-testid="edit-weight" placeholder="Actual weight (kg)" value={editForm.weight} onChange={setE("weight")} />
              <input type="number" step="0.01" className={darkInput} data-testid="edit-chargeable" placeholder="Chargeable weight (kg)" value={editForm.chargeable_weight} onChange={setE("chargeable_weight")} />
              <input type="number" className={darkInput} data-testid="edit-total-value" placeholder="Declared value ₹" value={editForm.total_value} onChange={setE("total_value")} />
              <input type="number" className={darkInput} data-testid="edit-cod" placeholder="COD amount ₹" value={editForm.cod_amount} onChange={setE("cod_amount")} />
              <select className={darkInput} data-testid="edit-payment-type" value={editForm.payment_type} onChange={setE("payment_type")}>
                <option value="PREPAID">PREPAID</option><option value="COD">COD</option>
              </select>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Expected Delivery</label>
                <input type="date" className={darkInput} data-testid="edit-expected-delivery" value={editForm.expected_delivery} onChange={setE("expected_delivery")} />
              </div>
              <input className={`${darkInput} sm:col-span-2`} data-testid="edit-notes" placeholder="Notes" value={editForm.notes} onChange={setE("notes")} />
            </div>
            <p className="mb-2 mt-4 text-[10px] font-bold uppercase tracking-wider text-slate-500">Consignee (Deliver To)</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <input className={darkInput} data-testid="edit-del-name" placeholder="Name" value={editForm.del_name} onChange={setE("del_name")} />
              <input className={darkInput} data-testid="edit-del-phone" placeholder="Phone (+ allowed)" value={editForm.del_phone} onChange={setE("del_phone")} />
              <input className={`${darkInput} sm:col-span-2`} data-testid="edit-del-address" placeholder="Address" value={editForm.del_address} onChange={setE("del_address")} />
              <input className={darkInput} data-testid="edit-del-city" placeholder="City" value={editForm.del_city} onChange={setE("del_city")} />
              <input className={darkInput} data-testid="edit-del-zipcode" placeholder="Zipcode" value={editForm.del_zipcode} onChange={setE("del_zipcode")} />
              <input className={`${darkInput} sm:col-span-2`} data-testid="edit-del-country" placeholder="Country" value={editForm.del_country} onChange={setE("del_country")} />
            </div>
            <p className="mb-2 mt-4 text-[10px] font-bold uppercase tracking-wider text-slate-500">Sender (Pickup From)</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <input className={darkInput} data-testid="edit-snd-name" placeholder="Name" value={editForm.snd_name} onChange={setE("snd_name")} />
              <input className={darkInput} data-testid="edit-snd-phone" placeholder="Phone" value={editForm.snd_phone} onChange={setE("snd_phone")} />
              <input className={`${darkInput} sm:col-span-2`} data-testid="edit-snd-address" placeholder="Address" value={editForm.snd_address} onChange={setE("snd_address")} />
              <input className={darkInput} data-testid="edit-snd-city" placeholder="City" value={editForm.snd_city} onChange={setE("snd_city")} />
              <input className={darkInput} data-testid="edit-snd-pincode" placeholder="Pincode" value={editForm.snd_pincode} onChange={setE("snd_pincode")} />
              <input className={`${darkInput} sm:col-span-2`} data-testid="edit-snd-state" placeholder="State" value={editForm.snd_state} onChange={setE("snd_state")} />
            </div>
            {editForm.tracking_number && (
              <div className="mt-4" data-testid="admin-live-tracking">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">Live Tracking (17TRACK)</p>
                {liveLoading ? <p className="text-xs text-slate-500">Fetching live scans…</p> : (
                  <div className="max-h-48 space-y-2.5 overflow-y-auto rounded-lg border border-adminborder p-3">
                    {[...liveEvents].sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || "")).map((ev, i) => (
                      <div key={i} className="border-l-2 border-saffron/50 pl-2.5" data-testid={`admin-live-event-${i}`}>
                        <p className="text-xs font-bold text-white">{ev.status}</p>
                        {ev.description && <p className="text-[11px] text-slate-400">{ev.description}</p>}
                        <p className="text-[10px] text-slate-500">
                          {ev.location}{ev.timestamp ? ` · ${new Date(ev.timestamp).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}` : ""}
                        </p>
                      </div>
                    ))}
                    {!liveEvents.length && <p className="text-xs text-slate-500">No scan events yet — they appear after the carrier's first scan.</p>}
                  </div>
                )}
              </div>
            )}
            {((editIntl.box_photos || []).length > 0 || (editIntl.payment_proofs || []).length > 0) && (
              <div className="mt-4">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">Box Photos & Payment Proofs</p>
                <div className="flex flex-wrap gap-2">
                  {[...(editIntl.box_photos || []), ...(editIntl.payment_proofs || [])].map((p, i) => (
                    <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}?auth=${localStorage.getItem("if_token")}`} target="_blank" rel="noreferrer" data-testid={`edit-photo-${i}`}
                       className="rounded-lg border border-adminborder px-3 py-1.5 text-xs font-bold text-blue-400 hover:border-blue-400">Photo {i + 1}</a>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setEditIntl(null)} className="rounded-lg border border-adminborder px-4 py-2 text-sm font-bold text-slate-300">Cancel</button>
              <button onClick={saveEdit} data-testid="intl-edit-save-btn" className="rounded-lg bg-saffron px-5 py-2 text-sm font-bold text-white hover:bg-saffron-hover">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/60 p-4" data-testid="status-update-modal">
          <div className={`${card} my-8 w-full max-w-md p-6`}>
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-heading text-lg font-bold text-white">Tracking — <span className="font-mono text-saffron">{editing.awb}</span></h3>
              <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-bold text-white" data-testid="tracking-popup-status">{trackLive?.status || editing.status}</span>
            </div>

            <div className="mt-4" data-testid="tracking-popup-timeline">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Shipment Timeline {trackLive?.live ? <span className="ml-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold text-emerald-400">LIVE</span> : null}
                </p>
                <button onClick={() => fetchLive(editing)} data-testid="tracking-refresh-btn"
                        className="flex items-center gap-1 rounded-lg border border-adminborder px-2.5 py-1 text-[10px] font-bold text-slate-300 hover:border-saffron hover:text-saffron">
                  <RefreshCw className={`h-3 w-3 ${trackLiveLoading ? "animate-spin" : ""}`} /> Refresh
                </button>
              </div>
              {trackLiveLoading && !trackLive ? <p className="text-xs text-slate-500">Fetching latest scans…</p> : (
                <div className="max-h-52 space-y-2.5 overflow-y-auto rounded-lg border border-adminborder p-3">
                  {[...(trackLive?.tracking || [])].sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || "")).map((ev, i) => (
                    <div key={i} className="border-l-2 border-saffron/50 pl-2.5" data-testid={`tracking-popup-event-${i}`}>
                      <p className="text-xs font-bold text-white">{ev.status}</p>
                      {ev.description && <p className="text-[11px] text-slate-400">{ev.description}</p>}
                      <p className="text-[10px] text-slate-500">
                        {ev.location}{ev.timestamp ? ` · ${new Date(ev.timestamp).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}` : ""}
                      </p>
                    </div>
                  ))}
                  {!(trackLive?.tracking || []).length && <p className="text-xs text-slate-500">No scan events yet — they appear after the carrier's first scan.</p>}
                </div>
              )}
            </div>

            <p className="mb-2 mt-5 text-[10px] font-bold uppercase tracking-wider text-slate-500">Manual Status Update</p>
            <div className="space-y-3">
              <select data-testid="status-update-select" className={darkInput} value={update.status}
                      onChange={(e) => setUpdate({ ...update, status: e.target.value })}>
                {STATUSES.filter(Boolean).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input className={darkInput} data-testid="status-update-location" placeholder="Location (e.g. Mumbai Hub)"
                     value={update.location} onChange={(e) => setUpdate({ ...update, location: e.target.value })} />
              <input className={darkInput} data-testid="status-update-description" placeholder="Scan description"
                     value={update.description} onChange={(e) => setUpdate({ ...update, description: e.target.value })} />
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => { setEditing(null); setTrackLive(null); }} className="rounded-lg border border-adminborder px-4 py-2 text-sm font-bold text-slate-300">Close</button>
              <button onClick={saveStatus} data-testid="status-update-save-btn"
                      className="rounded-lg bg-saffron px-4 py-2 text-sm font-bold text-white hover:bg-saffron-hover">Save Status</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
